function isNumber(n) {
  return typeof n === 'number' && Number.isFinite(n)
}

function hasPosition(node) {
  return !!node && isNumber(node.position?.x) && isNumber(node.position?.y)
}

function looksLikeReactFlowNode(node) {
  if (!hasPosition(node)) return false
  if (node?.type === 'default') return true
  if (typeof node?.data?.label === 'string') return true
  if (node?.data?.params && typeof node.data.params === 'object' && !Array.isArray(node.data.params)) return true
  return false
}

function normalizeReactFlowNodeData(data) {
  const safe = data && typeof data === 'object' ? data : {}
  const params = safe.params && typeof safe.params === 'object' && !Array.isArray(safe.params) ? safe.params : {}

  const output_name =
    typeof safe.output_name === 'string'
      ? safe.output_name
      : typeof params.output_name === 'string'
        ? params.output_name
        : null

  const cleanedParams = { ...params }
  delete cleanedParams.output_name

  return {
    ...safe,
    params: cleanedParams,
    output_name,
  }
}

function buildLevels(nodeIds, edges) {
  const inDegree = new Map()
  const out = new Map()

  for (const id of nodeIds) {
    inDegree.set(id, 0)
    out.set(id, [])
  }

  for (const e of edges) {
    const s = e.source
    const t = e.target
    if (!inDegree.has(s) || !inDegree.has(t)) continue
    out.get(s).push(t)
    inDegree.set(t, (inDegree.get(t) || 0) + 1)
  }

  const queue = []
  for (const [id, deg] of inDegree.entries()) {
    if (deg === 0) queue.push(id)
  }

  const level = new Map()
  for (const id of nodeIds) level.set(id, 0)

  let processed = 0
  while (queue.length) {
    const id = queue.shift()
    processed += 1
    const nexts = out.get(id) || []
    for (const n of nexts) {
      level.set(n, Math.max(level.get(n) || 0, (level.get(id) || 0) + 1))
      inDegree.set(n, (inDegree.get(n) || 0) - 1)
      if (inDegree.get(n) === 0) queue.push(n)
    }
  }

  if (processed !== nodeIds.length) return null
  return level
}

function fallbackGridPositions(nodeIds) {
  const cols = 4
  const colWidth = 280
  const rowHeight = 140
  const pos = new Map()

  nodeIds.forEach((id, i) => {
    pos.set(id, {
      x: 80 + (i % cols) * colWidth,
      y: 80 + Math.floor(i / cols) * rowHeight,
    })
  })

  return pos
}

function layeredPositions(nodeIds, edges) {
  const level = buildLevels(nodeIds, edges)
  if (!level) return fallbackGridPositions(nodeIds)

  const columns = new Map()
  for (const id of nodeIds) {
    const l = level.get(id) || 0
    if (!columns.has(l)) columns.set(l, [])
    columns.get(l).push(id)
  }

  const colWidth = 300
  const rowHeight = 140
  const pos = new Map()

  const levels = Array.from(columns.keys()).sort((a, b) => a - b)
  for (const l of levels) {
    const ids = columns.get(l)
    ids.forEach((id, idx) => {
      pos.set(id, {
        x: 80 + l * colWidth,
        y: 80 + idx * rowHeight,
      })
    })
  }

  return pos
}

function stripPresentationFields(obj) {
  if (!obj || typeof obj !== 'object') return {}
  const { label: _label, subtitle: _subtitle, type: _type, params, ...rest } = obj
  if (params && typeof params === 'object' && !Array.isArray(params)) return { ...params, ...rest }
  return rest
}

function toBackendNodeType(node) {
  if (!node) return null
  if (typeof node.type === 'string' && node.type !== 'default') return node.type
  if (typeof node.data?.type === 'string') return node.data.type
  if (typeof node.data?.label === 'string') return node.data.label
  return null
}

export function adaptDagToReactFlow(dag, { preservePositionsFrom = [] } = {}) {
  const rawNodes = dag?.nodes || []
  const rawEdges = dag?.edges || []

  // Persisted v12 graphs may carry UI state separately.
  const uiPositions = dag?.ui?.positionsById

  // If it already looks like React Flow nodes/edges, normalize and pass through.
  if (rawNodes.length && rawNodes.every((n) => looksLikeReactFlowNode(n))) {
    const nodes = rawNodes.map((n) => ({
      ...n,
      data: normalizeReactFlowNodeData(n?.data),
    }))
    return { nodes, edges: rawEdges }
  }

  const preservedPos = new Map()

  if (uiPositions && typeof uiPositions === 'object') {
    for (const [id, pos] of Object.entries(uiPositions)) {
      if (!id) continue
      if (pos && isNumber(pos.x) && isNumber(pos.y)) preservedPos.set(String(id), { x: pos.x, y: pos.y })
    }
  }

  for (const n of preservePositionsFrom) {
    if (n?.id && hasPosition(n)) preservedPos.set(n.id, n.position)
  }

  const nodeIds = rawNodes.map((n) => n.id).filter(Boolean)
  const layoutPos = layeredPositions(nodeIds, rawEdges)

  const nodes = rawNodes.map((n) => {
    const position = preservedPos.get(n.id) || layoutPos.get(n.id) || { x: 80, y: 80 }
    const rawData = n.data && typeof n.data === 'object' ? n.data : {}
    const output_name = typeof rawData.output_name === 'string' ? rawData.output_name : null
    const params = { ...rawData }
    delete params.output_name

    return {
      id: String(n.id),
      type: 'default',
      position,
      data: {
        label: n.type,
        type: n.type,
        params,
        output_name,
      },
    }
  })

  const edges = rawEdges
    .filter((e) => e && e.source && e.target)
    .map((e, i) => {
      const id = e.id || `${e.source}:${e.source_port || ''}->${e.target}:${e.target_port || ''}#${i}`
      const source_port = e.source_port || e.sourceHandle
      const target_port = e.target_port || e.targetHandle
      return {
        id: String(id),
        source: String(e.source),
        target: String(e.target),
        // Keep backend semantic ports for round-tripping.
        sourceHandle: source_port || 'out',
        targetHandle: target_port || 'in',
        data: {
          source_port: source_port || null,
          target_port: target_port || null,
          kind: e.type || null,
        },
      }
    })

  return { nodes, edges }
}

export function toBackendDag({ nodes = [], edges = [] } = {}) {
  const backendNodes = (nodes || [])
    .filter((n) => n && n.id)
    .map((n) => {
      const type = toBackendNodeType(n)
      const params = stripPresentationFields(n.data)

      if (params && typeof params === 'object') {
        const out = params.output_name
        if (typeof out !== 'string' || out.trim() === '') delete params.output_name
      }

      return {
        id: String(n.id),
        type,
        data: params,
      }
    })

  const backendEdges = (edges || [])
    .filter((e) => e && e.source && e.target)
    .map((e) => {
      const source_port = e.source_port || e.sourceHandle || e.data?.source_port || null
      const target_port = e.target_port || e.targetHandle || e.data?.target_port || null
      return {
        source: String(e.source),
        target: String(e.target),
        source_port,
        target_port,
        type: e.data?.kind || e.kind || e.type || 'data',
      }
    })

  return { nodes: backendNodes, edges: backendEdges }
}
